<template>
  <el-select
    ref="selectRef"
    v-model="selectedValue"
    v-bind="$attrs"
    :filterable="true"
    :remote="true"
    :remote-method="handleSearch"
    :loading="loading"
    :clearable="clearable"
    :placeholder="placeholder"
    :multiple="multiple"
    @visible-change="handleVisibleChange"
    @change="handleChange"
    @clear="handleClear"
    :disabled="disabled"
  >
    <el-option
      v-for="item in options"
      :key="getValueKey(item)"
      :label="getLabel(item)"
      :value="getValue(item)"
      :disabled="item.disabled"
    >
      <slot name="option" :item="item">
        {{ getLabel(item) }}
      </slot>
    </el-option>

    <!-- 加载更多 -->
    <div v-if="hasMore && showLoadMore" class="load-more">
      <el-button v-if="!loadingMore" type="text" size="small" @click="loadMore">
        加载更多
      </el-button>
      <span v-else class="loading-text">
        <el-icon class="is-loading"><Loading /></el-icon>
        加载中...
      </span>
    </div>

    <!-- 空状态 -->
    <template v-if="showEmpty" #empty>
      <div class="empty-content">
        <slot name="empty" v-bind="{ searchQuery, loading }">
          <el-icon v-if="emptyIcon" :size="emptyIconSize">
            <component :is="emptyIcon" />
          </el-icon>
          <span class="empty-text">
            {{ emptyText }}
          </span>
        </slot>
      </div>
    </template>
  </el-select>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { ElSelect, ElOption, ElButton, ElIcon } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'

const props = defineProps({
  // 双向绑定值
  modelValue: {
    type: [String, Number, Array, Object],
    default: ''
  },
  // 远程搜索方法
  // 必须返回 Promise<{ list: any[], total?: number, hasMore?: boolean }>
  remoteMethod: {
    type: Function,
    required: true
  },
  // 是否多选
  multiple: {
    type: Boolean,
    default: false
  },
  // 是否显示清除按钮
  clearable: {
    type: Boolean,
    default: true
  },
  // 占位符
  placeholder: {
    type: String,
    default: '请选择'
  },
  // 是否禁用
  disabled: {
    type: Boolean,
    default: false
  },
  // 每页大小
  pageSize: {
    type: Number,
    default: 20
  },
  // 延迟搜索时间（防抖）
  debounceTime: {
    type: Number,
    default: 300
  },
  // 最小搜索字符数
  minSearchLength: {
    type: Number,
    default: 0
  },
  // 是否在组件挂载时加载数据
  initLoad: {
    type: Boolean,
    default: false
  },
  // 空状态显示文本
  emptyText: {
    type: String,
    default: '暂无数据'
  },
  // 搜索无结果文本
  noResultText: {
    type: String,
    default: '无匹配结果'
  },
  // 空状态图标
  emptyIcon: {
    type: String,
    default: ''
  },
  // 空状态图标大小
  emptyIconSize: {
    type: Number,
    default: 20
  },
  // 值字段名
  valueKey: {
    type: String,
    default: 'value'
  },
  // 显示字段名
  labelKey: {
    type: String,
    default: 'label'
  },
  // 是否显示加载更多按钮
  showLoadMore: {
    type: Boolean,
    default: true
  },
  // 是否保留搜索值
  keepSearchQuery: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([
  'update:modelValue',
  'change',
  'visible-change',
  'clear',
  'search',
  'load-more'
])

// 响应式数据
const selectRef = ref(null)
const loading = ref(false)
const loadingMore = ref(false)
const options = ref([])
const searchQuery = ref('')
const currentPage = ref(1)
const hasMore = ref(true)
const total = ref(0)
const timer = ref(null)

// 计算属性
const selectedValue = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value)
    emit('change', value)
  }
})

const showEmpty = computed(() => {
  if (loading.value) return false
  return options.value.length === 0
})

const emptyText = computed(() => {
  if (searchQuery.value && options.value.length === 0) {
    return props.noResultText
  }
  return props.emptyText
})

// 方法
const getValue = (item) => {
  if (typeof item === 'object' && item !== null) {
    return item[props.valueKey]
  }
  return item
}

const getLabel = (item) => {
  if (typeof item === 'object' && item !== null) {
    return item[props.labelKey]
  }
  return item
}

const getValueKey = (item) => {
  const value = getValue(item)
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

// 远程搜索（防抖处理）
const handleSearch = (query) => {
  searchQuery.value = query

  // 检查最小搜索长度
  if (props.minSearchLength > 0 && query.length < props.minSearchLength) {
    options.value = []
    hasMore.value = false
    return
  }

  // 防抖处理
  if (timer.value) {
    clearTimeout(timer.value)
  }

  timer.value = setTimeout(() => {
    currentPage.value = 1
    loadData(query, 1)
  }, props.debounceTime)
}

// 加载数据
const loadData = async (query = '', page = 1) => {
  // 如果是加载更多，显示加载更多状态
  if (page > 1) {
    loadingMore.value = true
  } else {
    loading.value = true
  }
  try {
    const params = {
      keyword: query,
      pageNo: page,
      pageSize: props.pageSize
    }
    const result = await props.remoteMethod(params)
    if (page === 1) {
      options.value = result.list || []
    } else {
      options.value = [...options.value, ...(result.list || [])]
    }

    // 更新分页状态
    if (result.total !== undefined) {
      total.value = result.total
      hasMore.value = options.value.length < result.total
    } else if (result.hasMore !== undefined) {
      hasMore.value = result.hasMore
    } else {
      // 默认根据返回数据数量判断
      hasMore.value = (result.list || []).length >= props.pageSize
    }

    // 触发搜索事件
    emit('search', {
      query,
      page,
      total: total.value,
      hasMore: hasMore.value
    })
  } catch (error) {
    console.error('远程搜索失败:', error)
    if (page === 1) {
      options.value = []
    }
    hasMore.value = false
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

// 加载更多
const loadMore = () => {
  if (loadingMore.value || !hasMore.value) return

  currentPage.value += 1
  loadData(searchQuery.value, currentPage.value)
  emit('load-more', {
    query: searchQuery.value,
    page: currentPage.value
  })
}

// 重置搜索
const resetSearch = () => {
  searchQuery.value = ''
  currentPage.value = 1
  options.value = []
  hasMore.value = true
}

// 事件处理
const handleVisibleChange = (visible) => {
  if (!visible && !props.keepSearchQuery) {
    resetSearch()
  }
  emit('visible-change', visible)
}

const handleChange = (value) => {
  emit('change', value)
}

const handleClear = () => {
  if (!props.keepSearchQuery) {
    resetSearch()
  }
  emit('clear')
}

// 重新加载数据
const reload = () => {
  currentPage.value = 1
  loadData(searchQuery.value, 1)
}

// 根据值设置选项（用于回显）
const setOptionsFromValue = async () => {
  if (!props.modelValue) return

  const value = props.modelValue
  const values = props.multiple && Array.isArray(value) ? value : [value]
  for (const val of values) {
    const exists = options.value.find((item) => getValue(item) == val)
    if (!exists) {
      try {
        const result = await props.remoteMethod({
          keyword: '',
          pageNo: 1,
          pageSize: 1,
          id: props.modelValue
        })

        if (result.list && result.list.length > 0) {
          const item = result.list[0]
          if (getValue(item) == val) {
            options.value = [item, ...options.value]
          }
        }
      } catch (error) {
        console.error('获取选项失败:', error)
      }
    }
  }
}

// 公开的方法
defineExpose({
  // Element Plus 原生方法
  focus: () => selectRef.value?.focus(),
  blur: () => selectRef.value?.blur(),
  clear: () => {
    selectedValue.value = props.multiple ? [] : null
    if (selectRef.value?.clear) {
      selectRef.value.clear()
    }
  },

  // 自定义方法
  resetSearch,
  reload,
  loadMore,
  getOptions: () => options.value,
  getSearchQuery: () => searchQuery.value,
  getPageInfo: () => ({
    page: currentPage.value,
    total: total.value,
    hasMore: hasMore.value
  })
})

// 监听值变化，处理回显
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        setOptionsFromValue()
      })
    }
  },
  { immediate: true }
)

// 生命周期
onMounted(() => {
  if (props.initLoad) {
    loadData('', 1)
  }
})

// 组件卸载时清理定时器
import { onUnmounted } from 'vue'
onUnmounted(() => {
  if (timer.value) {
    clearTimeout(timer.value)
  }
})
</script>

<style scoped>
.load-more {
  padding-top: 8px;
  text-align: center;
  border-top: 1px solid var(--el-border-color-light);
}

.loading-text {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.loading-text .el-icon {
  margin-right: 4px;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  color: var(--el-text-color-secondary);
}

.empty-text {
  margin-top: 8px;
  font-size: 14px;
}

/* 调整下拉框最大高度 */
:deep(.el-select-dropdown__list) {
  max-height: 400px;
}
</style>
